import { useCallback, useEffect, useMemo, useState } from 'react';
import { Rocket, Search, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Switch } from './ui/switch';
import { villageUx } from '../lib/api';
import { fmtStamp } from './LiveClock';

export default function MerchantQuickLaunch({ storeId, onChanged }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(true);
  const [q, setQ] = useState('');
  const [busyKey, setBusyKey] = useState(null);

  const load = useCallback(() => {
    if (storeId) villageUx.quickLaunch(storeId).then(setData).catch(() => toast.error('Could not load Quick Launch list'));
  }, [storeId]);
  useEffect(() => { load(); }, [load]);

  const items = useMemo(() => {
    const term = q.trim().toLowerCase();
    return (data?.items || []).filter((i) => !term || i.name.toLowerCase().includes(term) || i.name_hi.includes(term));
  }, [data, q]);

  const patchItem = (key, patch) => setData((d) => ({
    ...d,
    items: d.items.map((i) => (i.key === key ? { ...i, ...patch } : i)),
  }));

  const toggle = async (item, on) => {
    setBusyKey(item.key);
    try {
      const r = await villageUx.toggle(storeId, item.key, on);
      patchItem(item.key, on
        ? { stocked: true, stocked_at: r.at, updated_at: r.product.updated_at, price: r.product.price, product_name: r.product.name }
        : { stocked: false, stocked_at: null, updated_at: null, price: item.default_price, product_name: null });
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not update'); }
    finally { setBusyKey(null); }
  };

  const savePrice = async (item, value) => {
    const price = parseFloat(value);
    if (!price || price <= 0 || price === item.price) return;
    try {
      const r = await villageUx.setPrice(storeId, item.key, price);
      setData((d) => ({ ...d, items: d.items.map((i) => (i.key === item.key ? { ...i, price: r.price, updated_at: r.updated_at } : i)) }));
      toast.success(`${item.name}: ₹${r.price}`);
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save price'); }
  };

  if (!data) return null;
  const stockedCount = data.items.filter((i) => i.stocked).length;
  const pct = Math.round((stockedCount / data.total) * 100);
  return (
    <section data-testid="merchant-quick-launch" className="mt-6 rounded-2xl border-2 border-[#E67E22] bg-[#FFF8EE]">
      <button data-testid="quick-launch-toggle-panel" onClick={() => setOpen(!open)} className="w-full p-4 flex items-center gap-3 text-left">
        <span className="w-11 h-11 rounded-xl bg-[#E67E22] text-white grid place-items-center shrink-0"><Rocket className="w-5 h-5"/></span>
        <span className="flex-1 min-w-0">
          <span className="block text-[10px] uppercase tracking-[0.2em] font-black text-[#D35400]">Quick Launch · Top 50 village items</span>
          <span data-testid="quick-launch-progress-text" className="block font-heading font-black text-lg">{stockedCount} of {data.total} stocked</span>
          <span className="block h-2 mt-1.5 rounded-full bg-white overflow-hidden"><span className="block h-full bg-[#1B5E20] transition-[width] duration-500" style={{ width: `${pct}%` }}/></span>
        </span>
        {open ? <ChevronUp className="w-5 h-5"/> : <ChevronDown className="w-5 h-5"/>}
      </button>
      {open && (
        <div className="px-4 pb-4">
          <p className="text-xs text-ink/60">One tap to stock. Default price is filled in — change it anytime.</p>
          <div className="mt-2 flex items-center gap-2 bg-white rounded-lg border-2 border-ink/10 px-3 h-10">
            <Search className="w-4 h-4 text-ink/40"/>
            <input data-testid="quick-launch-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search atta, dal, साबुन…" className="flex-1 outline-none text-sm bg-transparent"/>
          </div>
          <ul className="mt-3 grid sm:grid-cols-2 gap-2 max-h-[460px] overflow-y-auto pr-1">
            {items.map((i) => <QuickItem key={i.key} item={i} busy={busyKey === i.key} onToggle={toggle} onPrice={savePrice}/>)}
          </ul>
        </div>
      )}
    </section>
  );
}

function QuickItem({ item, busy, onToggle, onPrice }) {
  return (
    <li data-testid={`quick-item-${item.key}`} className={`rounded-xl border-2 p-2.5 flex items-center gap-2 bg-white ${item.stocked ? 'border-[#1B5E20]' : 'border-ink/10'}`}>
      <div className="flex-1 min-w-0">
        <div className="font-bold text-sm truncate">{item.name} <span className="text-ink/50 font-normal">{item.name_hi}</span></div>
        <div className="flex items-center gap-1 text-[11px] text-ink/60 mt-0.5">
          ₹<input data-testid={`quick-price-${item.key}`} type="number" min="1" defaultValue={item.price} key={`${item.key}-${item.price}`}
            disabled={!item.stocked} onBlur={(e) => onPrice(item, e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
            className="w-16 border border-ink/15 rounded px-1 h-6 text-ink font-bold disabled:bg-transparent disabled:border-transparent"/>
          / {item.unit}
        </div>
        {item.stocked && item.product_name && item.product_name !== item.name && (
          <div className="text-[10px] text-ink/50 truncate">Linked: {item.product_name}</div>
        )}
        {item.stocked && item.stocked_at && (
          <div data-testid={`quick-stamp-${item.key}`} className="text-[10px] text-[#1B5E20] mt-0.5">
            {item.updated_at && item.updated_at !== item.stocked_at ? 'Updated' : 'Stocked'} {fmtStamp(item.updated_at || item.stocked_at)}
          </div>
        )}
      </div>
      {busy ? <Loader2 className="w-5 h-5 animate-spin text-ink/40"/> : (
        <div className="flex flex-col items-center gap-0.5">
          <Switch data-testid={`quick-switch-${item.key}`} checked={item.stocked} onCheckedChange={(v) => onToggle(item, v)}/>
          <span className="text-[9px] font-black uppercase tracking-wider text-ink/50">{item.stocked ? 'In shop' : 'Stock it'}</span>
        </div>
      )}
    </li>
  );
}
